import { Button, Flex, Select, SearchField } from '@backstage/ui';
import { RiCloseLine, RiTeamFill, RiShapesFill } from '@remixicon/react';
import { type FilterOption } from './filters';

const toSelectedValues = (keys: unknown): string[] =>
  Array.isArray(keys) ? keys.map(String) : [];

const smallSelectStyle = { flex: 1, minWidth: 0 };
const searchStyle = { flex: 2, minWidth: 0 };

type DefinitionListFilterBarProps = {
  /** Label for the owner select (e.g. "Check owner" / "Track owner"). */
  ownerLabel: string;
  ownerOptions: FilterOption[];
  selectedOwners: string[];
  onOwnersChange: (owners: string[]) => void;
  kindOptions: FilterOption[];
  selectedKinds: string[];
  onKindsChange: (kinds: string[]) => void;
  search: { value: string; onChange: (value: string) => void };
  onClear: () => void;
};

/**
 * Filter controls for the check and track list views: an owner select, an
 * affected-entity-kind select, a free-word search field and a Clear button.
 * Owner and kind selections are URL-backed by the caller so they are shareable.
 */
export const DefinitionListFilterBar = ({
  ownerLabel,
  ownerOptions,
  selectedOwners,
  onOwnersChange,
  kindOptions,
  selectedKinds,
  onKindsChange,
  search,
  onClear,
}: DefinitionListFilterBarProps) => {
  const hasAnySelection =
    selectedOwners.length > 0 ||
    selectedKinds.length > 0 ||
    search.value.length > 0;

  return (
    <Flex align="center" gap="4" style={{ width: '100%', flexWrap: 'wrap' }}>
      <SearchField
        aria-label="Search"
        placeholder="Search..."
        value={search.value}
        onChange={search.onChange}
        style={searchStyle}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
      />
      <Select
        placeholder="Entity kind"
        selectionMode="multiple"
        searchable
        options={kindOptions}
        value={selectedKinds}
        icon={<RiShapesFill />}
        onChange={keys => onKindsChange(toSelectedValues(keys))}
        style={smallSelectStyle}
      />
      <Select
        placeholder={ownerLabel}
        selectionMode="multiple"
        searchable
        options={ownerOptions}
        value={selectedOwners}
        icon={<RiTeamFill />}
        onChange={keys => onOwnersChange(toSelectedValues(keys))}
        style={smallSelectStyle}
      />
      <Button
        variant="tertiary"
        iconStart={<RiCloseLine />}
        isDisabled={!hasAnySelection}
        onClick={onClear}
      >
        Clear
      </Button>
    </Flex>
  );
};
